import { useEffect, useState } from 'react';
import { useWallet } from '../context/WalletContext';
import { useContracts } from './useContract';

const ZERO = '0x0000000000000000000000000000000000000000';

const EMPTY = {
  isOwner: false,
  isAdmin: false,
  isPrivileged: false,   // ← Navbar gate. NEVER becomes true unless verified below.
  owner: null,
  admin: null,
  loading: false,
  verified: false,       // ← true only after a successful on-chain check.
};

/**
 * Verify whether the connected wallet is owner or admin of the storage contract.
 *
 * Guarantees:
 *  - `isPrivileged` starts as false on every account change (no stale flash).
 *  - `isPrivileged` becomes true ONLY when an on-chain call succeeds AND the
 *    lowercased address matches owner/admin exactly (zero address never matches).
 *  - On any RPC error, state stays non-privileged. No admin UI can leak.
 */
export function useAdminStatus() {
  const { account, isCorrectNetwork } = useWallet();
  const { read } = useContracts();

  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    // Hard reset — no admin UI is possible until a fresh positive check completes.
    if (!account || !isCorrectNetwork) {
      setState(EMPTY);
      return;
    }

    setState({ ...EMPTY, loading: true });

    let cancelled = false;
    Promise.all([read.storage.owner(), read.storage.admin()])
      .then(([ownerRaw, adminRaw]) => {
        if (cancelled) return;

        const acc = String(account || '').toLowerCase();
        const ownerStr = String(ownerRaw || '').toLowerCase();
        const adminStr = String(adminRaw || '').toLowerCase();

        // Explicit checks. Zero address never matches; empty never matches.
        const isOwner =
          acc !== '' &&
          ownerStr !== '' &&
          ownerStr !== ZERO &&
          ownerStr === acc;

        const isAdmin =
          acc !== '' &&
          adminStr !== '' &&
          adminStr !== ZERO &&
          adminStr === acc;

        setState({
          isOwner,
          isAdmin,
          isPrivileged: isOwner || isAdmin,
          owner: ownerRaw,
          admin: adminRaw,
          loading: false,
          verified: true,
        });
      })
      .catch(() => {
        if (cancelled) return;
        // RPC/decoding failure → NON-PRIVILEGED. Never leaks admin.
        setState(EMPTY);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, isCorrectNetwork]);

  return state;
}
